<template>
    <div>
        <div class="success-hero">
            <div class="success-hero__emoji">🎉</div>
            <div>
                <p class="success-hero__title">You are registered. Thanks!</p>
                <p class="success-hero__subtitle">See you at Food Distribution, {{ volunteerData.name }}.</p>
            </div>
        </div>

        <div class="info-card">
            <div class="info-card__rows">
                <div class="info-row">
                    <span class="info-row__icon">📍</span>
                    <span>{{ locationAddress }}</span>
                </div>
                <div class="info-row">
                    <span class="info-row__icon">📅</span>
                    <span>{{ formatDayOfWeek(currentFD.date) }}, {{ formatDate(currentFD.date) }}</span>
                </div>
                <div class="info-row">
                    <span class="info-row__icon">🕐</span>
                    <span><b>{{ locationTime }}</b> hours</span>
                </div>
            </div>
            <div class="success-actions">
                <button class="button is-rounded calendar-btn" @click="addToCalendar">
                    <span class="icon"><i class="fas fa-calendar-plus"></i></span>
                    <span>Add to calendar</span>
                </button>
                <button class="button is-rounded share-btn" @click="shareSignUpLink">
                    <span class="icon"><i class="fas fa-share-nodes"></i></span>
                    <span>Share sign-up link</span>
                </button>
                <a href="https://wa.me/6596140431"
                   target="_blank" rel="noopener"
                   class="button is-rounded contact-btn">
                    <span class="icon"><i class="fab fa-whatsapp"></i></span>
                    <span>Contact us</span>
                </a>
            </div>
        </div>

        <OfferHelpBanner />
    </div>
</template>

<script>
import OfferHelpBanner from '@/components/volunteer/OfferHelpBanner.vue';

export default {
    components: { OfferHelpBanner },
    computed: {
        volunteerData() {
            return this.$store.getters['volunteer/getVolunteerData'];
        },
        currentFD() {
            return this.$store.getters['fd/getCurrentFD'];
        },
        tbrTime() {
            if (!this.currentFD || !this.currentFD.time) return '--:--';
            const [hours, minutes] = this.currentFD.time.split(':').map(Number);
            const total = hours * 60 + minutes + 45;
            const h = String(Math.floor(total / 60) % 24).padStart(2, '0');
            const m = String(total % 60).padStart(2, '0');
            return `${h}:${m}`;
        },
        locationAddress() {
            return this.volunteerData && this.volunteerData.location === 'TBR'
                ? '32 Telok Blangah Rise'
                : 'Void Deck, 89 Redhill Close';
        },
        locationTime() {
            return this.volunteerData && this.volunteerData.location === 'TBR'
                ? this.tbrTime
                : (this.currentFD ? this.currentFD.time || '13:00' : '13:00');
        }
    },
    methods: {
        formatDayOfWeek(timestamp) {
            if (!timestamp) return '';
            const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
            return date.toLocaleDateString('en-GB', { weekday: 'long', timeZone: 'Asia/Singapore' });
        },
        formatDate(timestamp) {
            if (!timestamp) return '';
            const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
            return date.toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric', timeZone: 'Asia/Singapore' });
        },
        async shareSignUpLink() {
            const fdId = this.currentFD && this.currentFD.id;
            const url = `${window.location.origin}${window.location.pathname}#/volunteer/${fdId}`;
            const text = `Join me in volunteering at the Apex Food Distribution! Sign up here:`;
            if (navigator.share) {
                try {
                    await navigator.share({ title: 'Volunteer Sign-Up', text, url });
                } catch {
                    // user cancelled
                }
            } else {
                window.open(`https://wa.me/?text=${encodeURIComponent(text + ' ' + url)}`, '_blank', 'noopener');
            }
        },
        addToCalendar() {
            const fd = this.currentFD;
            const parts = new Intl.DateTimeFormat('en-CA', {
                timeZone: 'Asia/Singapore',
                year: 'numeric', month: '2-digit', day: '2-digit'
            }).formatToParts(fd.date.toDate());
            const part = (type) => Number(parts.find(p => p.type === type).value);

            const timeStr = this.locationTime;
            const [hour, minute] = timeStr.split(':').map(Number);

            const startUTC = new Date(Date.UTC(part('year'), part('month') - 1, part('day'), hour - 8, minute || 0));
            const endUTC = new Date(startUTC.getTime() + 2.5 * 60 * 60 * 1000);

            const stamp = (d) => d.toISOString().replace(/[-:]/g, '').replace(/\.\d{3}/, '');

            const ics = [
                'BEGIN:VCALENDAR',
                'VERSION:2.0',
                'PRODID:-//Apex//Food Distribution//EN',
                'CALSCALE:GREGORIAN',
                'BEGIN:VEVENT',
                'UID:fd-' + (fd.id || stamp(startUTC)) + '@apexbt.org',
                'DTSTAMP:' + stamp(new Date()),
                'DTSTART:' + stamp(startUTC),
                'DTEND:' + stamp(endUTC),
                'SUMMARY:Apex Food Distribution (Volunteer)',
                'LOCATION:' + (this.volunteerData && this.volunteerData.location === 'TBR'
                    ? '32 Telok Blangah Rise'
                    : 'Void Deck\\, 89 Redhill Close'),
                'DESCRIPTION:You signed up to volunteer at the Apex Redhill Food Distribution.',
                'END:VEVENT',
                'END:VCALENDAR'
            ].join('\r\n');

            const blob = new Blob([ics], { type: 'text/calendar;charset=utf-8' });
            const url = URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = 'apex-food-distribution.ics';
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            URL.revokeObjectURL(url);
        }
    }
}
</script>

<style scoped>
.success-hero {
    border-radius: 14px;
    padding: 0.9rem 1.25rem;
    display: flex;
    align-items: center;
    gap: 0.75rem;
    color: #fff;
    background: linear-gradient(135deg, #10b981 0%, #14b8a6 55%, #0ea5e9 100%);
    box-shadow: 0 8px 20px -10px rgba(16, 185, 129, 0.65);
    animation: success-in 0.5s cubic-bezier(0.16, 1, 0.3, 1) both;
}

@keyframes success-in {
    from { opacity: 0; transform: translateY(10px) scale(0.98); }
    to   { opacity: 1; transform: translateY(0) scale(1); }
}

.success-hero__emoji {
    font-size: 1.6rem;
    line-height: 1;
    flex-shrink: 0;
}

.success-hero__title {
    font-size: 1rem;
    font-weight: 700;
    line-height: 1.2;
}

.success-hero__subtitle {
    font-size: 0.82rem;
    opacity: 0.92;
    margin-top: 0.15rem;
}

.info-card {
    background: #fff;
    border-radius: 16px;
    padding: 1.25rem 1.35rem;
    margin-top: 1rem;
    box-shadow: 0 6px 20px -12px rgba(15, 23, 42, 0.25);
    border: 1px solid #eef0f4;
}

.info-card__rows {
    display: flex;
    flex-direction: column;
    gap: 0.6rem;
}

.info-row {
    display: flex;
    align-items: center;
    gap: 0.6rem;
    font-size: 1rem;
    color: #374151;
}

.info-row__icon {
    font-size: 1.15rem;
}

.success-actions {
    display: flex;
    flex-direction: column;
    gap: 0.6rem;
    margin-top: 1rem;
}

.calendar-btn {
    width: 100%;
    height: 2.85rem;
    font-weight: 700;
    color: #fff;
    border: none;
    background: linear-gradient(135deg, #14b8a6 0%, #0ea5e9 100%);
    box-shadow: 0 10px 22px -10px rgba(14, 165, 233, 0.7);
    transition: transform 0.12s ease, box-shadow 0.12s ease;
}

.calendar-btn:hover {
    transform: translateY(-2px);
    box-shadow: 0 14px 26px -10px rgba(14, 165, 233, 0.8);
    color: #fff;
}

.share-btn {
    width: 100%;
    height: 2.85rem;
    font-weight: 700;
    color: #0f766e;
    border: 1.5px solid #14b8a6;
    background: #f0fdf4;
    transition: transform 0.12s ease, box-shadow 0.12s ease;
}

.share-btn:hover {
    transform: translateY(-2px);
    background: #ccfbf1;
    color: #0f766e;
}

.contact-btn {
    width: 100%;
    height: 2.85rem;
    font-weight: 700;
    color: #fff;
    border: none;
    background: #25d366;
    box-shadow: 0 8px 18px -8px rgba(37, 211, 102, 0.6);
    transition: transform 0.12s ease, box-shadow 0.12s ease;
}

.contact-btn:hover {
    transform: translateY(-2px);
    box-shadow: 0 12px 22px -8px rgba(37, 211, 102, 0.7);
    color: #fff;
}

@media (prefers-reduced-motion: reduce) {
    .success-hero { animation: none; }
}
</style>
